import { useEffect, useRef } from 'react';
import { useAssistant } from './AssistantContext';

const BUBBLE_MAX_WIDTH = 300;
const BUBBLE_PADDING = '10px 16px';
const BUBBLE_FONT_SIZE = '17px';
const BUBBLE_LINE_HEIGHT = '24px';
const BUBBLE_SAFE_HEIGHT_BUFFER = 12;

export const useEyeAnimation = (eyeControls, blinkControls) => {
  const { state } = useAssistant();
  const { expression, config } = state;

  useEffect(() => {
    let isMounted = true;
    let lastAnimationTime = Date.now();

    const animateEyes = async () => {
      if (!isMounted || state.isRecording) return;

      if (expression === 'sleep') {
        await blinkControls.start({
          scaleY: 0.1,
          transition: { duration: config.animationSpeed.blink },
        });
        await eyeControls.start({
          x: 0,
          rotate: 0,
          transition: { duration: config.animationSpeed.eyes / 2 },
        });
        return;
      }

      if (expression === 'thinking') {
        await eyeControls.start({
          y: -5,
          transition: { duration: config.animationSpeed.eyes / 2 },
        });
        return;
      }

      if (expression === 'surprised') {
        await blinkControls.start({
          scaleY: 1.3,
          transition: { duration: config.animationSpeed.blink },
        });
        return;
      }

      const random = Math.random();

      try {
        const animState = blinkControls.getAnimationState?.();
        const currentScaleY = animState?.scaleY?.current;
        if (!currentScaleY || currentScaleY < 0.9) {
          await blinkControls.start({
            scaleY: 1,
            transition: { duration: config.animationSpeed.blink * 3 },
          });
        }
      } catch (e) {
        await blinkControls.start({
          scaleY: 1,
          transition: { duration: config.animationSpeed.blink },
        });
      }

      if (random < 0.7) {
        await eyeControls.start({
          x: 6,
          rotate: 15,
          transition: { duration: config.animationSpeed.eyes },
        });
        await eyeControls.start({
          x: -6,
          rotate: -15,
          transition: { duration: config.animationSpeed.eyes },
        });
        await eyeControls.start({
          x: 0,
          rotate: 0,
          y: 0,
          transition: { duration: config.animationSpeed.eyes },
        });
      } else {
        await blinkControls.start({
          scaleY: 0.1,
          transition: { duration: config.animationSpeed.blink },
        });
        await blinkControls.start({
          scaleY: 1,
          transition: { duration: config.animationSpeed.blink },
        });
      }
    };

    const scheduleAnimation = () => {
      if (!isMounted) return;
      const currentTime = Date.now();
      if (currentTime - lastAnimationTime >= config.blinkFrequency) {
        animateEyes();
        lastAnimationTime = currentTime;
      }
      requestAnimationFrame(scheduleAnimation);
    };

    requestAnimationFrame(scheduleAnimation);

    return () => {
      isMounted = false;
    };
  }, [expression, eyeControls, blinkControls, config, state.isRecording]);
};

export const useSleepMode = () => {
  const { state, actions } = useAssistant();
  const sleepTimerRef = useRef(null);

  useEffect(() => {
    const checkSleep = () => {
      const timeSinceInteraction = Date.now() - state.lastInteraction;
      if (
        timeSinceInteraction >= state.config.sleepDelay &&
        state.expression !== 'sleep' &&
        !state.bubbleText
      ) {
        actions.setExpression('sleep');
        actions.setBubbleText('zzz');
      }
    };

    if (sleepTimerRef.current) {
      clearInterval(sleepTimerRef.current);
    }

    sleepTimerRef.current = setInterval(checkSleep, 10000);

    return () => {
      if (sleepTimerRef.current) {
        clearInterval(sleepTimerRef.current);
      }
    };
  }, [state.lastInteraction, state.expression, state.bubbleText, state.config.sleepDelay, actions]);

  return {
    wakeUp: actions.updateLastInteraction,
  };
};

export const useTextTypingEffect = () => {
  const { state, actions } = useAssistant();
  const typingTimerRef = useRef(null);
  const bubbleAppearTimerRef = useRef(null);
  const autoDismissTimerRef = useRef(null);
  const messageTokenRef = useRef(0);
  const measureElementRef = useRef(null);

  const clearTimers = () => {
    clearTimeout(typingTimerRef.current);
    clearTimeout(bubbleAppearTimerRef.current);
    clearTimeout(autoDismissTimerRef.current);
  };

  const ensureMeasureElement = () => {
    if (measureElementRef.current) return measureElementRef.current;

    const el = document.createElement('div');
    el.className = 'bubble';
    el.style.visibility = 'hidden';
    el.style.position = 'absolute';
    el.style.top = '-9999px';
    el.style.left = '-9999px';
    el.style.pointerEvents = 'none';
    el.style.whiteSpace = 'normal';
    el.style.wordWrap = 'break-word';
    document.body.appendChild(el);
    measureElementRef.current = el;
    return el;
  };

  useEffect(() => {
    return () => {
      clearTimers();
      if (measureElementRef.current) {
        document.body.removeChild(measureElementRef.current);
        measureElementRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!state.currentMessage?.id || !state.currentMessage?.text) return;

    const token = ++messageTokenRef.current;
    clearTimers();
    actions.clearCancelTyping();

    const fullText = state.currentMessage.text;
    const measureEl = ensureMeasureElement();
    measureEl.textContent = fullText;
    // Use block display for accurate text wrapping simulation matching actual bubble rendering
    measureEl.style.display = 'block';
    measureEl.style.maxWidth = `${BUBBLE_MAX_WIDTH}px`;
    measureEl.style.padding = BUBBLE_PADDING;
    measureEl.style.fontSize = BUBBLE_FONT_SIZE;
    measureEl.style.lineHeight = BUBBLE_LINE_HEIGHT;
    measureEl.style.boxSizing = 'border-box';

    const rect = measureEl.getBoundingClientRect();

    // Calculate size once based on full text, then lock it
    // Small buffer (12px) accounts for browser rendering variance
    actions.setBubbleSize({
      width: Math.ceil(rect.width),
      height: Math.ceil(rect.height) + BUBBLE_SAFE_HEIGHT_BUFFER,
      fixed: true,
    });
    actions.setBubbleText('');
    actions.setTyping(true);

    const typeNextCharacter = (index = 0) => {
      if (messageTokenRef.current !== token) return;
      if (state.cancelTyping) return;

      if (index < fullText.length) {
        actions.setBubbleText(fullText.substring(0, index + 1));
        typingTimerRef.current = setTimeout(
          () => typeNextCharacter(index + 1),
          state.config.textTypingSpeed
        );
        return;
      }

      actions.setBubbleText(fullText);
      actions.setTyping(false);
      actions.completeSpeech(state.currentMessage.id, 'typed');

      if (state.messageVariant === 'sticky') {
        return;
      }

      const autoHideMs =
        state.currentMessage.autoHideMs || Math.min(4000 + fullText.length * 30, 8000);
      autoDismissTimerRef.current = setTimeout(() => {
        if (messageTokenRef.current !== token) return;
        actions.dismiss('auto_hidden');
      }, autoHideMs);
    };

    bubbleAppearTimerRef.current = setTimeout(() => {
      if (messageTokenRef.current !== token) return;
      typeNextCharacter(0);
    }, 350);

    return () => {
      clearTimers();
    };
  }, [
    state.currentMessage?.id,
    state.currentMessage?.text,
    state.currentMessage?.autoHideMs,
    state.messageVariant,
    state.config.textTypingSpeed,
    state.cancelTyping,
    actions,
  ]);

  useEffect(() => {
    if (!state.cancelTyping) return;

    clearTimers();
    actions.dismiss('cancelled');
    actions.clearCancelTyping();
  }, [state.cancelTyping, actions]);

  return null;
};
